import { DownloadIcon } from 'lucide-react'
import { ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { Link } from './Link'

interface IProps {
    source: string
    ext: string
}

const imgExts = [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'bmp',
    'svg',
    'webp',
    'ico',
    'apng',
] as const
const pdfExts = ['pdf'] as const
const textExts = ['txt'] as const
const docExts = [
    'doc',
    'docx',
    'odt',
    'xls',
    'xlsx',
    'ods',
    'ppt',
    'pptx',
    'odp',
] as const
const videoExts = ['mp4', 'webm', 'mkv'] as const

export function isImage(ext: string) {
    return imgExts.includes(ext as (typeof imgExts)[number])
}

function AnexoIndisponivel({ source }: { source?: string }) {
    return (
        <div className="flex min-h-40 w-full flex-col items-center justify-center gap-3 text-center">
            <p className="text-lg text-muted-foreground">
                Não foi possível carregar o anexo
            </p>
            {source && (
                <Link
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5"
                >
                    <ExternalLinkIcon size={16} />
                    Abrir em outra página
                </Link>
            )}
        </div>
    )
}

function PreviewImagem({ source }: { source: string }) {
    const [erro, setErro] = useState(false)

    if (erro) return <AnexoIndisponivel source={source} />

    return (
        <img
            src={source}
            alt={String(source)}
            style={{
                width: '100%',
                height: 'auto',
                maxHeight: '100%',
            }}
            loading="lazy"
            onError={() => setErro(true)}
        />
    )
}

export default function PreviewAnexo({ source, ext }: IProps) {
    if (!source) return <AnexoIndisponivel />

    return match(ext)
        .with(...imgExts, () => <PreviewImagem source={source} />)
        .with(...pdfExts, () => (
            <embed
                src={source}
                type="application/pdf"
                style={{
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '5/4',
                    maxHeight: '100%',
                }}
            />
        ))
        .with(...textExts, () => (
            <iframe
                src={source}
                style={{
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '16/10',
                    maxHeight: '100%',
                }}
            />
        ))
        .with(...docExts, () => (
            <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    source,
                )}`}
                loading="lazy"
                style={{
                    width: '100%',
                    height: 'auto',
                    aspectRatio: '4/3',
                    maxHeight: '100%',
                }}
            />
        ))
        .with(...videoExts, () => (
            <video
                controls
                autoPlay={false}
                style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '100%',
                }}
            >
                <source src={source} type="video/mp4" />
            </video>
        ))
        .otherwise(() => (
            <div className="flex min-h-40 w-full flex-col items-center justify-center gap-3 text-center">
                <p className="text-lg text-muted-foreground">
                    Não foi possível carregar pŕevia do anexo
                </p>
                {source && (
                    <Link
                        href={source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5"
                    >
                        <ExternalLinkIcon size={16} />
                        Abrir em outra página
                    </Link>
                )}
            </div>
        ))
}
